import type { FilterValue, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import React, {
  createContext, FC, useContext, useEffect, useReducer,
} from 'react';
import type { Dayjs } from 'dayjs';
import { AssetType } from '@ynomia/core/dist/blueprint';
import {
  ActionFromPayloadMap, AssetStatusFilter, AssetUpdate, ToggleAssetStatus, ToggleObservation,
} from '../config/types';
import FilterReducer from '../reducers/assetManager';
import {
  ASSET_MANAGER_STATE_URL_KEY,
  generatePrunedClone,
  readAssetManagerStateFromUrl,
  serializeAssetManagerState,
} from '../utils/assetManagerUrlState';
import { getAssetTypes } from '../selectors';
import { getContextStores } from '.';

export type AssetFilter = {
  searchTags: Array<string>
  searchText: string,
  dateFilter: [Dayjs, Dayjs] | []
  assetStatusFilter: AssetStatusFilter
  observationTypeFilter: Record<string, ToggleObservation>,
  multiSearchModeFilter: 'or' | 'and'
  selectedLayerIds: Array<string | null>
  openedLayerId: string | null
  tableFilter: Record<string, FilterValue | null>
  tableSorter: SorterResult<AssetUpdate> | null
  pagination: TablePaginationConfig
  fieldFilters: { [key: string]: Array<string> }
  timeTravelDate: Dayjs | null
  selectedBandIds: { [key: string]: Array<string> }
  lastFilterUpdate?: Date
};

export type Collapsable = Record<string, {
  isCollapsed?: boolean
  showAll?: boolean
}>;

export type AssetSelection = {
  lastManualSelectionUpdate: Date | undefined
  selectedAssets: Array<string>
  hoveredAssetId: string | undefined
  hoverOrigin: 'twin' | 'webhq' | undefined
  showSelectedOnly: boolean
};

export type AssetTypeOptions = {
  filters: AssetFilter
  assetSelection: AssetSelection
  collapsable?: Collapsable
  selectedPlanId?: string | null
  showPlanView?: boolean
};

export type AssetManagerStateType = {
  [id: string]: {
    assetManagerAssetTypes: {
      [assetTypeId: string]: AssetTypeOptions
    }
    selectedAssetTypeId: string
  }
};

const initialState: AssetManagerStateType = readAssetManagerStateFromUrl() || {};

type WithManagerId = { assetManagerId: string };

export type AssetManagerActionPayloadMap = {
  INITIALIZE_ASSET_MANAGER: WithManagerId & { assetTypes: AssetType[]; defaultAssetTypeId: string };
  SET_SEARCH_TAGS: WithManagerId & { searchTags: string[] };
  SET_SEARCH_TEXT: WithManagerId & { searchText: string };
  SET_DATE_FILTER: WithManagerId & { dateFilter: [Dayjs, Dayjs] | [] };
  SET_SELECTED_PLAN_BAND_IDS: WithManagerId & { planId: string; currentSelectedBandIds: string[] };
  SET_TIME_TRAVEL_DATE: WithManagerId & { timeTravelDate: Dayjs | null };
  SET_SELECTED_ASSET_TYPE_ID: WithManagerId & { selectedAssetTypeId: string };
  SELECT_ASSET_STATUS_FILTER: WithManagerId & { id: string; toggledValue: ToggleAssetStatus };
  SELECT_OBSERVATION_TYPE_FILTER: WithManagerId & { id: string; toggledValue: ToggleObservation };
  SET_ASSET_LAYER_FILTER: WithManagerId & { selectedLayerIds: (string | null)[] };
  SET_OPENED_LAYER_ID_FILTER: WithManagerId & { openedLayerId: string | null | undefined };
  SET_FIELD_FILTERS: WithManagerId & { key: string; value: string[] };
  SET_TABLE_FILTER: WithManagerId & { tableFilter: AssetFilter['tableFilter'] };
  SET_TABLE_SORTER: WithManagerId & { tableSorter: SorterResult<AssetUpdate> | null };
  SET_TABLE_PAGINATION: WithManagerId & { pagination: TablePaginationConfig };
  SET_MULTI_SEARCH_MODE_FILTER: WithManagerId & { multiSearchModeFilter: 'or' | 'and' };
  SET_SELECTED_ASSETS: WithManagerId & { selectedAssets: string[]; manual: boolean };
  SET_HOVERED_ASSET_ID: WithManagerId & {
    hoveredAssetId: string | undefined; hoverOrigin: 'twin' | 'webhq'
  };
  SET_SHOW_SELECTED_ONLY: WithManagerId & { showSelectedOnly: boolean };
  SET_COLLAPSABLE: WithManagerId & { id: string; state: Collapsable[string] };
  SET_SELECTED_PLAN_ID: WithManagerId & { selectedPlanId: string | null };
  SET_SHOW_PLAN_VIEW: WithManagerId & { showPlanView: boolean };
  REMOVE_ALL_FILTERS: WithManagerId;
  REMOVE_SELECTED_PLAN_BAND_IDS: WithManagerId;
  REMOVE_ASSET_STATUS_FILTER: WithManagerId & { id: string };
  REMOVE_SELECTED_PLAN_BAND_ID: WithManagerId & { id: string; planId: string };
  REMOVE_ALL_ASSET_STATUS_FILTER: WithManagerId;
  REMOVE_ALL_OBSERVATION_TYPE_FILTER: WithManagerId;
  REMOVE_SELECTED_LAYER_IDS_FILTER: WithManagerId;
  REMOVE_OPENED_LAYER_ID_FILTER: WithManagerId;
  RESET_SELECTED_ASSETS: WithManagerId;
};

export type AssetManagerAction = ActionFromPayloadMap<AssetManagerActionPayloadMap>;

export const AssetManagerContext = createContext({
  state: initialState,
  dispatch: (() => { }) as React.Dispatch<AssetManagerAction>,
});

export const useAssetManager = (): [AssetManagerStateType, React.Dispatch<AssetManagerAction>] => {
  const { state, dispatch } = useContext(AssetManagerContext);
  return [state, dispatch];
};

export const AssetManagerProvider:FC<React.PropsWithChildren<{}>> = ({ children }) => {
  /* Context  */
  const contextStores = getContextStores();
  const [state, dispatch] = useReducer(FilterReducer, initialState);
  const assetTypes = getAssetTypes(contextStores);
  const defaultAssetType = assetTypes?.[0]?.id;
  const defaultSelectedPlanId = assetTypes?.[0]?.plans?.[0]?.id;

  useEffect(() => {
    const url = new URL(window.location.href);
    const clone = generatePrunedClone(state, defaultAssetType, defaultSelectedPlanId);
    const newValue = serializeAssetManagerState(clone);
    const currentValue = url.searchParams.get(ASSET_MANAGER_STATE_URL_KEY);
    if (newValue === currentValue) return;
    if (newValue === null) {
      url.searchParams.delete(ASSET_MANAGER_STATE_URL_KEY);
    } else {
      url.searchParams.set(ASSET_MANAGER_STATE_URL_KEY, newValue);
    }
    window.history.replaceState(null, '', url);
  }, [state]);
  return (
    <AssetManagerContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </AssetManagerContext.Provider>
  );
};
